import { DeleteOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import type { AnyObject } from "antd/es/_util/type";
import type { ColumnType } from "antd/es/table";
import { TableActions, type TableAction } from "./components/TableActions";
import type { Client, CurrentUser, Ticket, Turbine } from "../app/generated-axios-client";
import { Flex, Typography } from "antd";

export const defaultSort = (sortKey: string, currentFilter?: AnyObject) => {
  const sort: string | undefined = currentFilter?.sort;
  if (!sort) {
    return undefined;
  }
  const descending = sort.startsWith("-");
  const sortedKey = descending ? sort.slice(1) : sort;
  if (sortedKey !== sortKey) {
    return undefined;
  }
  return descending ? "descend" : "ascend";
};

// sortField is the server-side ordering term for columns whose displayed value comes from a
// relation, e.g. the "client" column renders a legal name but must sort by client__legal_name.
export const simpleColumn = (
  colName: string,
  titleKeyword: string,
  intl: IntlShape,
  currentFilter?: AnyObject,
  sortField?: string
): ColumnType => {
  const sortKey = sortField ?? colName;
  return {
    dataIndex: colName,
    key: sortKey,
    title: intl.formatMessage({ id: titleKeyword }, { count: 1 }),
    defaultSortOrder: defaultSort(sortKey, currentFilter),
    sorter: !!currentFilter,
  };
};

export const actionsValue = (
  client?: Client,
  ticket?: Ticket,
  turbine?: Turbine,
  user?: CurrentUser,
  onView?: () => void,
  onEdit?: () => void,
  onDelete?: () => void,
  actions: TableAction[] | undefined = undefined,
  entityLabel?: string
) => {
  const disabled = () => {
    if (user && ticket) {
      return !(user.is_staff || ticket.created_by === user.id);
    }
  };

  const allActions = [];
  if (onView) {
    allActions.push({
      key: "view",
      icon: <EyeOutlined />,
      onClick: onView,
      tooltip: entityLabel ? `View ${entityLabel}` : undefined,
    });
  }
  if (onEdit) {
    allActions.push({
      key: "edit",
      icon: <EditOutlined />,
      onClick: onEdit,
      tooltip: entityLabel ? `Edit ${entityLabel}` : undefined,
    });
  }
  if (onDelete) {
    allActions.push({
      disabled: disabled(),
      key: "delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: onDelete,
      tooltip: entityLabel ? `Delete ${entityLabel}` : undefined,
    });
  }

  return <TableActions actions={actions ? actions.concat(allActions) : allActions} />;
};

export const dateTimeValue = (item: string, intl: IntlShape, format = "short_date") => (
  <Flex vertical>
    <Typography.Text ellipsis>
      {intl.formatMessage({ id: format }, { value: new Date(item) })}
    </Typography.Text>
    <Typography.Text type="secondary">
      {intl.formatMessage({ id: "time" }, { value: new Date(item) })}
    </Typography.Text>
  </Flex>
);

export const dateValue = (item: string, intl: IntlShape, format = "short_date") => (
  <Typography.Text ellipsis>
    {intl.formatMessage({ id: format }, { value: new Date(item) })}
  </Typography.Text>
);
